import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useVerifyOtp, useSendOtp } from '../hooks/useQueries';
import { trackEvent } from '../utils/analytics';
import './Login.css';

const OTP_LENGTH = 6;

const VerifyOtp = () => {
  const [otp, setOtp]             = useState(Array(OTP_LENGTH).fill(''));
  const [otpError, setOtpError]   = useState('');
  const [resendMsg, setResendMsg] = useState('');
  const [timer, setTimer]         = useState(90);
  const [canResend, setCanResend] = useState(false);
  const [success, setSuccess]     = useState(false);

  const inputsRef = useRef([]);
  const navigate  = useNavigate();
  const location  = useLocation();
  const email     = location.state?.email || '';

  const { mutateAsync: verifyOtpFn, isPending: verifying } = useVerifyOtp();
  const { mutateAsync: sendOtpFn,   isPending: resending  } = useSendOtp();

  useEffect(() => {
    if (!email) navigate('/login');
  }, [email, navigate]);

  useEffect(() => {
    if (timer <= 0) { setCanResend(true); return; }
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  const focusInput = (idx) => inputsRef.current[idx]?.focus();

  const handleChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    setOtpError('');
    setResendMsg('');
    if (val && idx < OTP_LENGTH - 1) focusInput(idx + 1);
    if (val && idx === OTP_LENGTH - 1 && next.every(Boolean)) {
      submitOtp(next.join(''));
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      const next = [...otp];
      next[idx - 1] = '';
      setOtp(next);
      focusInput(idx - 1);
    }
    if (e.key === 'ArrowLeft'  && idx > 0)               focusInput(idx - 1);
    if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1)  focusInput(idx + 1);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
    if (pasted.length === OTP_LENGTH) submitOtp(pasted);
  };

  const submitOtp = async (code = otp.join('')) => {
    if (code.length < OTP_LENGTH || !code.split('').every(Boolean)) {
      setOtpError('Please enter the complete 6-digit OTP.');
      return;
    }
    setOtpError('');
    try {
      await verifyOtpFn({ email, otp: code });
      trackEvent('LOGIN', { metadata: { email } });
      setSuccess(true);
      setTimeout(() => navigate('/'), 1800);
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        'Invalid OTP. Please check and try again.';
      setOtpError(apiMsg);
      setOtp(Array(OTP_LENGTH).fill(''));
      setTimeout(() => focusInput(0), 100);
    }
  };

  const handleResend = async () => {
    if (!canResend || resending) return;
    setResendMsg('');
    setOtpError('');
    try {
      await sendOtpFn(email);
      setOtp(Array(OTP_LENGTH).fill(''));
      setCanResend(false);
      setTimer(90);
      setResendMsg('A new OTP has been sent to your email.');
      focusInput(0);
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        'Could not resend OTP. Please try again.';
      setOtpError(apiMsg);
    }
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 4)) + c)
    : '';

  const allFilled = otp.every(Boolean);

  return (
    <div className="auth-page">
      <div className="auth-bg">
        <div className="auth-bg__circle auth-bg__circle--1" />
        <div className="auth-bg__circle auth-bg__circle--2" />
        <div className="auth-bg__circle auth-bg__circle--3" />
      </div>

      <div className="auth-card-wrap">
        {/* ── Left — brand ── */}
        <div className="auth-brand">
          <Link to="/" className="auth-brand__logo">
            <img
              src="https://res.cloudinary.com/dfl6zlu16/image/upload/v1784294408/Untitled_design_cnrpon.png"
              alt="F Creation"
            />
          </Link>
          <h2 className="auth-brand__title">One step away<br />from gifting joy</h2>
          <p className="auth-brand__sub">Enter the code we just sent and you're in!</p>
          <div className="auth-brand__features">
            {[
              { icon: '🔒', text: 'Secure one-time password login' },
              { icon: '⚡', text: 'No passwords to remember' },
              { icon: '📧', text: 'Code delivered instantly to your inbox' },
              { icon: '🛡️', text: 'Your account stays protected' },
            ].map((f, i) => (
              <div key={i} className="auth-feature">
                <span className="auth-feature__icon">{f.icon}</span>
                <span className="auth-feature__text">{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right — OTP ── */}
        <div className="auth-form-panel">
          <div className="auth-form-inner">
            {success ? (
              <div style={{ textAlign: 'center', animation: 'fadeUp 0.4s ease' }}>
                <div className="otp-success-icon">🎉</div>
                <h2 className="auth-form-title" style={{ textAlign: 'center' }}>You're in!</h2>
                <p className="auth-form-sub" style={{ textAlign: 'center' }}>
                  Welcome back! Taking you home…
                </p>
                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
                  <span
                    className="auth-spinner"
                    style={{
                      borderColor: 'var(--border)',
                      borderTopColor: 'var(--primary)',
                      width: 28, height: 28, borderWidth: 3,
                    }}
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="auth-form-header">
                  <div className="auth-form-icon">📩</div>
                  <h1 className="auth-form-title">Check your inbox</h1>
                  <p className="otp-email-hint">
                    We sent a 6-digit code to <strong>{maskedEmail}</strong>.<br />
                    Enter it below to sign in.
                  </p>
                </div>

                <div className="otp-inputs" onPaste={handlePaste}>
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputsRef.current[idx] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      autoFocus={idx === 0}
                      className={[
                        'otp-input',
                        digit    ? 'otp-input--filled' : '',
                        otpError ? 'otp-input--error'  : '',
                      ].join(' ').trim()}
                      onChange={(e) => handleChange(e.target.value, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      disabled={verifying || success}
                      aria-label={`OTP digit ${idx + 1}`}
                    />
                  ))}
                </div>

                {otpError && (
                  <p className="auth-error" style={{ textAlign: 'center', justifyContent: 'center', marginTop: 10 }}>
                    {otpError}
                  </p>
                )}

                {resendMsg && !otpError && (
                  <p style={{ textAlign: 'center', fontSize: 12, color: '#16a34a', marginTop: 10 }}>
                    ✓ {resendMsg}
                  </p>
                )}

                <button
                  className={`auth-btn ${verifying ? 'auth-btn--loading' : ''}`}
                  style={{ marginTop: 24 }}
                  disabled={verifying || !allFilled}
                  onClick={() => submitOtp()}
                >
                  {verifying ? (
                    <><span className="auth-spinner" /> Verifying…</>
                  ) : (
                    <>Verify &amp; Sign In <span className="auth-btn-arrow">→</span></>
                  )}
                </button>

                <div className="otp-resend-row" style={{ marginTop: 20 }}>
                  {canResend ? (
                    <>
                      Didn't get it?{' '}
                      <button
                        className="otp-resend-btn"
                        onClick={handleResend}
                        disabled={resending}
                      >
                        {resending ? 'Sending…' : 'Resend OTP'}
                      </button>
                    </>
                  ) : (
                    <>
                      Resend code in <span className="otp-timer">0:{String(timer).padStart(2, '0')}</span>
                      <p style={{color:'red'}}>Didn't receive your OTP? Please check your Spam folder.</p>
                    </>
                  )}
                </div>

                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <Link to="/login" className="auth-back-link" style={{ justifyContent: 'center' }}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    Change email address
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;